import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useTheme } from '@mui/material/styles';

import { ThemeProps } from '@styles/theme';
import { isCompletePairingCode, PAIRING_CODE_ALPHABET, PAIRING_CODE_LENGTH } from 'AuthPart/DevicePairing/store';

import { useStyles } from './PairDeviceModal.styles';

export type CodeInputProps = {
    value: string;
    disabled?: boolean;
    hasError?: boolean;
    clearLabel: string;
    onChange: (value: string) => void;
    onComplete: (value: string) => void;
};

const isAllowed = (character: string) => PAIRING_CODE_ALPHABET.includes(character.toUpperCase());

/**
 * Six boxes, laid out 3 + 3 to match how the headset draws the code.
 * Characters outside the pairing alphabet are ignored rather than shown and rejected later —
 * there is no such thing as a valid code containing an O or a 1.
 */
export const CodeInput: React.FC<CodeInputProps> = ({
    value, disabled, hasError, clearLabel, onChange, onComplete,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const inputs = useRef<Array<HTMLInputElement | null>>([]);
    const characters = value.padEnd(PAIRING_CODE_LENGTH, ' ').slice(0, PAIRING_CODE_LENGTH).split('');

    const focusAt = (index: number) => {
        inputs.current[Math.max(0, Math.min(index, PAIRING_CODE_LENGTH - 1))]?.focus();
    };

    // The first box worth typing into: the first gap, or the start again once the code is full,
    // since a full code that came back rejected is usually retyped from the beginning.
    const focusNextEditable = () => {
        focusAt(value.length < PAIRING_CODE_LENGTH ? value.length : 0);
    };

    useEffect(() => {
        if (!disabled) {
            focusNextEditable();
        }
    }, [disabled]);

    // A rejected code should put the caret back where the correction starts rather than leaving
    // it parked on the last box.
    useEffect(() => {
        if (hasError && !disabled) {
            focusNextEditable();
        }
    }, [hasError]);

    /**
     * Writes characters starting at a box and moves on. One character for a keystroke, several
     * for a pasted code.
     */
    const writeAt = (index: number, typed: string[]) => {
        if (typed.length === 0) {
            return;
        }

        // Typing past the end appends rather than leaving a hole — a pairing code has no gaps,
        // so there is nowhere for a stray character to sit.
        const at = Math.min(index, value.length);

        const chars = value.padEnd(PAIRING_CODE_LENGTH, ' ').split('');
        typed.forEach((character, offset) => {
            if (at + offset < PAIRING_CODE_LENGTH) {
                chars[at + offset] = character;
            }
        });

        const next = chars.join('').slice(0, PAIRING_CODE_LENGTH).replace(/ +$/, '');

        onChange(next);
        focusAt(at + typed.length);

        // Only auto-submit a code that is actually whole — backspacing a middle box leaves a hole,
        // and a six-character string with a hole in it is not six characters.
        if (isCompletePairingCode(next)) {
            onComplete(next);
        }
    };

    const clear = () => {
        onChange('');
        focusAt(0);
    };

    const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.metaKey || event.ctrlKey || event.altKey) {
            return;
        }

        if (event.key === 'Backspace') {
            event.preventDefault();

            // Backspace on an empty box steps back and clears the previous one, which is what
            // people expect when correcting a mistyped code.
            const target = value[index] ? index : index - 1;
            if (target < 0) {
                return;
            }

            const chars = value.padEnd(PAIRING_CODE_LENGTH, ' ').split('');
            chars[target] = ' ';
            onChange(chars.join('').replace(/ +$/, ''));
            focusAt(target);

            return;
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            focusAt(index - 1);

            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            focusAt(index + 1);

            return;
        }

        // Take ownership of ordinary typing rather than letting the input handle it. Clicking into
        // a filled box leaves a caret rather than a selection, so the browser would either append
        // a second character to that box or — with maxLength in play — swallow the keystroke
        // entirely. Writing the character ourselves makes overtyping a box always work.
        if (event.key.length === 1) {
            event.preventDefault();

            if (isAllowed(event.key)) {
                writeAt(index, [event.key.toUpperCase()]);
            }
        }
    };

    // Soft keyboards and IMEs do not always report the character on keydown, so this is still the
    // path that catches them.
    const handleChange = (index: number, raw: string) => {
        writeAt(index, raw.split('').filter(isAllowed).map(c => c.toUpperCase()));
    };

    const handlePaste = (index: number, event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        handleChange(index, event.clipboardData.getData('text'));
    };

    return (
        <>
            <div className={styles.codeRow} role="group" aria-label="Pairing code">
                {
                    characters.map((character, index) => (
                        <React.Fragment key={index}>
                            {index === PAIRING_CODE_LENGTH / 2 && <span className={styles.codeGap} />}

                            <input
                                ref={element => { inputs.current[index] = element; }}
                                className={classNames(styles.codeBox, {
                                    [styles.codeBoxFilled]: character.trim().length > 0,
                                    [styles.codeBoxError]: hasError,
                                })}
                                value={character.trim()}
                                onChange={event => handleChange(index, event.target.value)}
                                onKeyDown={event => handleKeyDown(index, event)}
                                onPaste={event => handlePaste(index, event)}
                                onFocus={event => event.target.select()}
                                disabled={disabled}
                                inputMode="text"
                                autoComplete="one-time-code"
                                autoCapitalize="characters"
                                spellCheck={false}
                                aria-label={`Character ${index + 1}`}
                            />
                        </React.Fragment>
                    ))
                }
            </div>

            <div className={styles.clearRow}>
                {
                    value.length > 0 && (
                        <button
                            type="button"
                            className={styles.clearButton}
                            onClick={clear}
                            disabled={disabled}
                        >
                            {clearLabel}
                        </button>
                    )
                }
            </div>
        </>
    );
}
